import { ApolloError } from '@apollo/client'
import { message } from 'antd'
import {
    JOB_STATUS,
    JobStatusTagList,
    LISTS_PAGINATION_LIMIT,
} from '@ProdWb/constants'
import { ExclamationCircleOutlined } from '@ant-design/icons'

import {
    useUpdateJobRequestMutation,
    JobDetailsDocument,
    JobRequestDetailsDocument,
    useUpdateJobMutation,
    UpdateJobInput,
    SchedulingQueueDocument,
    useUpdateJobLinesMutation,
    useUpdateJobRequestLinesMutation,
    UpdateJobLineInput,
    UpdateJobRequestLineInput,
    useUpdateJobRequestQcTypeMutation,
    JobRequestQcType,
    useUpdateJobRequestStatusMutation,
    useCancelJobMutationMutation,
    JobRequestStatus,
    JobRequestRejectionInput,
    useUpdateJobVendorDueDateMutation,
    useUpdateJobRequestVendorDueDateMutation,
    useAddJobToActiveJobBoardMutation,
    JobMilestonesDocument,
    SalesOrderLineProductProcessesDocument,
    UnbundledSalesOrdersDocument,
} from '~/gql/graphql'
import Box from '~/core/Box'
import logger from '~/lib/logger'
import getColor from '~/lib/getColor'
import {
    ORDER_MILESTONES,
    ORDER_MILESTONE_TITLES,
} from '../../components/JobStatusProgressBar/constants'
import { ProgressDataType } from '../../components/JobStatusProgressBar/types'
import { TAG_COLORS } from '../../constants'

import {
    JobStatusTagMap,
    QueueItemStatusTagMap,
    QueueStatusTagMap,
    QUEUE_STATUS,
    TABS,
} from './constants'

export const MESSAGE_MODALS = {
    cancelJobError: {
        title: 'Job cannot be canceled',
        content: (
            <Box>
                This job has an accepted job request, to cancel this entire job
                the accepted job request must be canceled first.
            </Box>
        ),
        icon: (
            <ExclamationCircleOutlined
                style={{
                    color: getColor('error'),
                    fontSize: '20px',
                }}
            />
        ),
        width: '520px',
        okText: 'Ok',
    },
}

export const determineTab = (tab: string) => {
    if (TABS.indexOf(tab) === -1) {
        return TABS[0]
    }
    return tab
}

export const getQueueStatusTag = (
    status: string,
    queueItemCount: number,
    currentQueueItem: number,
) => {
    let tagTitle = ''

    if (status) {
        tagTitle =
            status === QUEUE_STATUS.PROGRESS || status === QUEUE_STATUS.PAUSED
                ? `${QueueStatusTagMap[status].title} ${currentQueueItem}/${queueItemCount}`
                : QueueStatusTagMap[status].title
    }

    return {
        title: tagTitle,
        color: status ? QueueStatusTagMap[status].color : '',
    }
}

export const getQueueItemStatusTag = (status: string) =>
    QueueItemStatusTagMap[status]

export const getJobStatusTag = (
    status: string,
    isForJobBoard?: boolean,
    isList?: boolean,
) => {
    const jobStatusTag = JobStatusTagMap[status]

    if (status === JOB_STATUS.CANCELED && isForJobBoard) {
        jobStatusTag.color = TAG_COLORS.RED
    }

    return isList ? JobStatusTagList[status] : JobStatusTagMap[status]
}

export function MutationOperation() {
    const [updateJob] = useUpdateJobMutation()
    const [updateJobRequest] = useUpdateJobRequestMutation()
    const [updateJobLineDetails] = useUpdateJobLinesMutation()
    const [updateJobRequestLineDetails] = useUpdateJobRequestLinesMutation()
    const [updateJobRequestQCType] = useUpdateJobRequestQcTypeMutation()
    const [updateJobRequestStatus] = useUpdateJobRequestStatusMutation()
    const [cancelJob] = useCancelJobMutationMutation()
    const [updateJobDueDate] = useUpdateJobVendorDueDateMutation()
    const [updateJobRequestDueDate] = useUpdateJobRequestVendorDueDateMutation()
    const [addJobToActiveJobBoard] = useAddJobToActiveJobBoardMutation()

    return {
        updateJob: (variables: { id: number; input: UpdateJobInput }) => {
            return updateJob({
                variables: { ...variables },
                refetchQueries: [
                    {
                        query: JobDetailsDocument,
                        variables: {
                            id: variables.id,
                        },
                        fetchPolicy: 'network-only',
                    },
                ],
            })
                .then(res => res.data)
                .catch(err => {
                    logger.exception(err, {
                        extra: {
                            info: 'Failed to update job note',
                            variables,
                        },
                    })
                })
        },
        updateJobRequest: (variables: {
            id: number
            input: { vendorNotes: string }
        }) => {
            return updateJobRequest({
                variables: { ...variables },
                refetchQueries: [
                    {
                        query: JobRequestDetailsDocument,
                        variables: {
                            id: variables.id,
                        },
                        fetchPolicy: 'network-only',
                    },
                ],
            })
                .then(res => res.data)
                .catch(err => {
                    logger.exception(err, {
                        extra: {
                            info: 'Failed to update job request vendor note',
                            variables,
                        },
                    })
                })
        },
        updateJobRequestStatus: (
            variables: {
                id: number
                status: JobRequestStatus
                rejectionInfo: JobRequestRejectionInput | null
            },
            jobId: number,
        ) => {
            return updateJobRequestStatus({
                variables: { ...variables },
                refetchQueries: [
                    {
                        query: SchedulingQueueDocument,
                        variables: { jobId },
                    },
                    {
                        query: JobDetailsDocument,
                        variables: { id: Number(jobId) },
                    },
                    {
                        query: JobRequestDetailsDocument,
                        variables: { id: variables.id },
                    },
                ],
            })
                .then(res => res.data)
                .catch((err: ApolloError) => {
                    logger.exception(err, {
                        extra: {
                            info: 'Failed to update job request status',
                            message: err.message,
                            variables,
                        },
                    })

                    message.error(err.message)
                })
        },
        cancelJob: (variables: {
            id: number
            salesOrderId: number | undefined
        }) => {
            return cancelJob({
                variables: { ...variables },
                refetchQueries: [
                    {
                        query: JobDetailsDocument,
                        variables: { id: Number(variables.id) },
                    },
                    {
                        query: SchedulingQueueDocument,
                        variables: { jobId: Number(variables.id) },
                    },
                    {
                        query: UnbundledSalesOrdersDocument,
                        variables: {
                            limit: LISTS_PAGINATION_LIMIT,
                            offset: 0,
                            sort: [],
                            filters: [],
                        },
                    },
                    {
                        query: SalesOrderLineProductProcessesDocument,
                        variables: {
                            salesOrderId: Number(variables.salesOrderId),
                            filters: [],
                        },
                    },
                ],
            })
                .then(response => {
                    return response
                })
                .catch(err => {
                    logger.exception(err, {
                        extra: {
                            info: 'Failed to cancel the job',
                            variables,
                        },
                    })
                    return err
                })
        },
        useUpdateJobDetails: (
            jobId: number,
            jobLineInput: UpdateJobLineInput[],
        ) => {
            return updateJobLineDetails({
                variables: {
                    jobId: jobId,
                    jobLineInput: jobLineInput,
                },
                refetchQueries: [
                    {
                        query: JobDetailsDocument,
                        variables: {
                            id: jobId,
                            mfgPartnerId: null,
                        },
                    },
                ],
            })
                .then(res => {
                    return res?.data
                })
                .catch(err => {
                    logger.exception(err, {
                        extra: {
                            info: 'Failed to update job line details',
                            variables: { jobId, jobLineInput },
                        },
                    })
                })
        },

        useUpdateJobRequestDetails: (
            jobRequestId: number,
            input: UpdateJobRequestLineInput[],
        ) => {
            return updateJobRequestLineDetails({
                variables: {
                    id: jobRequestId,
                    input: input,
                },
                refetchQueries: [
                    {
                        query: JobRequestDetailsDocument,
                        variables: {
                            id: jobRequestId,
                        },
                    },
                ],
            })
                .then(res => {
                    return res?.data
                })
                .catch(err => {
                    logger.exception(err, {
                        extra: {
                            info: 'Failed to update job request line details',
                            variables: { jobRequestId, input },
                        },
                    })
                })
        },

        useUpdateJobVendorDueDate: (jobId: number, vendorDueDate: Date) => {
            return updateJobDueDate({
                variables: {
                    id: jobId,
                    date: vendorDueDate,
                },
                refetchQueries: [
                    {
                        query: JobDetailsDocument,
                        variables: {
                            id: jobId,
                        },
                    },
                ],
            })
                .then(res => {
                    return res?.data
                })
                .catch(err => {
                    logger.exception(err, {
                        extra: {
                            info: 'Failed to update job vendor due date',
                            variables: { jobId, vendorDueDate },
                        },
                    })
                })
        },
        useUpdateJobRequestVendorDueDate: (
            jobRequestId: number,
            vendorDueDate: Date,
            adjustmentReason?: string,
            contextJobId?: number,
        ) => {
            const variables = {
                id: jobRequestId,
                vendorDueDate,
                vendorDueDateUpdateReason: adjustmentReason || null,
            }

            return updateJobRequestDueDate({
                variables,
                refetchQueries: [
                    {
                        query: JobRequestDetailsDocument,
                        variables: {
                            id: jobRequestId,
                        },
                    },
                    {
                        query: JobMilestonesDocument,
                        variables: {
                            id: contextJobId,
                        },
                    },
                ],
            })
                .then(res => {
                    return res?.data
                })
                .catch(err => {
                    logger.exception(err, {
                        extra: {
                            info: 'Failed to update job vendor due date',
                            variables: {
                                jobRequestId,
                                vendorDueDate,
                            },
                        },
                    })
                })
        },
        updateQCTypeForJobRequest: (id: number, qcType: JobRequestQcType) => {
            const variables = {
                id,
                qcType,
            }

            return updateJobRequestQCType({
                variables,
                refetchQueries: [
                    {
                        query: JobRequestDetailsDocument,
                        variables: {
                            id,
                        },
                    },
                ],
            })
                .then(res => {
                    return res?.data
                })
                .catch(err => {
                    logger.exception(err, {
                        extra: {
                            info: 'Failed to update the inspection Type for job request',
                            variables,
                        },
                    })
                })
        },
        addJobToActiveJobBoard: (jobId: number) => {
            return addJobToActiveJobBoard({
                variables: {
                    jobId,
                },
                refetchQueries: [
                    {
                        query: JobDetailsDocument,
                        variables: {
                            id: jobId,
                        },
                    },
                ],
                awaitRefetchQueries: true,
            })
                .then(res => {
                    message.success('Job added to live job board successfully')
                    return res?.data
                })
                .catch(err => {
                    const defaultErrorMessage =
                        'Failed to add job to live job board'

                    logger.exception(err, {
                        extra: {
                            info: defaultErrorMessage,
                            variables: { jobId },
                        },
                    })
                    message.error(
                        err?.graphQLErrors[0]?.message || defaultErrorMessage,
                    )
                    return err
                })
        },
    }
}

export const getFixedValue = (value: number, isPercentage = false) => {
    return isPercentage ? `${value.toFixed(2)}%` : `$${value.toFixed(2)}`
}

export const getProgressBarData = (milestones?: any, salesOrderDate?: Date) => {
    let milestoneData: ProgressDataType[] = []

    if (milestones) {
        milestoneData = [
            {
                milestone: ORDER_MILESTONES.ORDER_PLACED,
                milestoneTitle:
                    ORDER_MILESTONE_TITLES[ORDER_MILESTONES.ORDER_PLACED],
                actualDate: salesOrderDate,
            },
            {
                milestone: ORDER_MILESTONES.SCHEDULING,
                milestoneTitle:
                    ORDER_MILESTONE_TITLES[ORDER_MILESTONES.SCHEDULING],
                porDate: milestones[ORDER_MILESTONES.SCHEDULING].scheduledDate,
                committedDate:
                    milestones[ORDER_MILESTONES.SCHEDULING].committedDate,
                actualDate: milestones[ORDER_MILESTONES.SCHEDULING].actualDate,
            },
            {
                milestone: ORDER_MILESTONES.PRODUCTION,
                milestoneTitle:
                    ORDER_MILESTONE_TITLES[ORDER_MILESTONES.PRODUCTION],
                porDate: milestones[ORDER_MILESTONES.PRODUCTION].scheduledDate,
                committedDate:
                    milestones[ORDER_MILESTONES.PRODUCTION].committedDate,
                actualDate: milestones[ORDER_MILESTONES.PRODUCTION].actualDate,
            },
            {
                milestone: ORDER_MILESTONES.INSPECTION,
                milestoneTitle:
                    ORDER_MILESTONE_TITLES[ORDER_MILESTONES.INSPECTION],
                porDate: milestones[ORDER_MILESTONES.INSPECTION].scheduledDate,
                committedDate:
                    milestones[ORDER_MILESTONES.INSPECTION].committedDate,
                actualDate: milestones[ORDER_MILESTONES.INSPECTION].actualDate,
            },
            {
                milestone: ORDER_MILESTONES.DELIVERY_ORDER,
                milestoneTitle:
                    ORDER_MILESTONE_TITLES[ORDER_MILESTONES.DELIVERY_ORDER],
                porDate:
                    milestones[ORDER_MILESTONES.DELIVERY_ORDER].scheduledDate,
                committedDate:
                    milestones[ORDER_MILESTONES.DELIVERY_ORDER].committedDate,
                actualDate:
                    milestones[ORDER_MILESTONES.DELIVERY_ORDER].actualDate,
            },
            {
                milestone: ORDER_MILESTONES.SHIPPING,
                milestoneTitle:
                    ORDER_MILESTONE_TITLES[ORDER_MILESTONES.SHIPPING],
                porDate: milestones[ORDER_MILESTONES.SHIPPING].scheduledDate,
                committedDate:
                    milestones[ORDER_MILESTONES.SHIPPING].committedDate,
                actualDate: milestones[ORDER_MILESTONES.SHIPPING].actualDate,
            },
            {
                milestone: ORDER_MILESTONES.CUSTOMER_DELIVERY,
                milestoneTitle:
                    ORDER_MILESTONE_TITLES[ORDER_MILESTONES.CUSTOMER_DELIVERY],
                porDate:
                    milestones[ORDER_MILESTONES.CUSTOMER_DELIVERY]
                        .scheduledDate,
                committedDate:
                    milestones[ORDER_MILESTONES.CUSTOMER_DELIVERY]
                        .committedDate,
                actualDate:
                    milestones[ORDER_MILESTONES.CUSTOMER_DELIVERY].actualDate,
            },
        ] as ProgressDataType[]
    }

    return milestoneData
}
